import { classNames } from "primereact/utils";
import styles from "../WaitingListConfirmation.module.scss";
import { AuthenticationForm } from "../../../../common/components/authentication/AuthenticationForm/AuthenticationForm";
import { IConfirmWaitingListSpotResultModel } from "../models/waitingListConfirmationModels";

interface INavigationProps {
  onGoToMyReservations: () => void;
  onGoToCalendar: () => void;
}

const WaitingListEventLine = ({
  result,
}: {
  result: IConfirmWaitingListSpotResultModel;
}) =>
  result.WaitingListEventName ? (
    <div className={classNames(styles.conflictCard, styles.conflictCardHighlighted)}>
      <span className={styles.conflictCardLabel}>Свободно място в</span>
      <span className={styles.conflictCardTitle}>{result.WaitingListEventName}</span>
      {result.WaitingListEventDateText && (
        <span className={styles.conflictCardMeta}>
          {result.WaitingListEventDateText}
        </span>
      )}
    </div>
  ) : null;

interface IWaitingListConflictProps {
  result: IConfirmWaitingListSpotResultModel;
  onReplace: () => void;
  onKeep: () => void;
}

/** Asks which reservation to keep: the existing one or the spot that opened on the waiting list. */
export const WaitingListConflict = ({
  result,
  onReplace,
  onKeep,
}: IWaitingListConflictProps) => {
  const reservations = result.ConflictingReservations ?? [];
  const canReplace = !!result.CanReplaceConflictingReservations;
  const isSingle = reservations.length === 1;

  return (
    <div className={styles.container}>
      <div className={styles.successAnimation}>
        <h1 className={styles.successTitle}>Освободи се място!</h1>
        <p className={styles.successSubtitle}>
          {result.Message ||
            (isSingle
              ? "Имате друга резервация по същото време. Изберете коя да запазите."
              : "Имате други резервации по същото време. Изберете дали да ги запазите, или да заемете освободеното място.")}
        </p>
      </div>

      <div className={styles.conflictList}>
        <WaitingListEventLine result={result} />

        <span className={styles.conflictListHeading}>
          {isSingle ? "Текуща резервация" : "Текущи резервации"}
        </span>
        {reservations.map((reservation) => (
          <div
            key={reservation.ReservationId}
            className={styles.conflictCard}
            data-testid="conflicting-reservation"
          >
            <span className={styles.conflictCardTitle}>{reservation.EventName}</span>
            <span className={styles.conflictCardMeta}>{reservation.StudioName}</span>
            <span className={styles.conflictCardMeta}>{reservation.DateText}</span>
            {!reservation.CanBeCancelled && reservation.CannotCancelReason && (
              <span className={styles.conflictCardWarning}>
                {reservation.CannotCancelReason}
              </span>
            )}
          </div>
        ))}

        <p className={styles.conflictNote}>
          {canReplace
            ? isSingle
              ? "Ако смените резервацията, текущата ще бъде отменена и ще заемете освободеното място."
              : "Ако смените резервацията, текущите ще бъдат отменени и ще заемете освободеното място."
            : "Текущата ви резервация не може да бъде отменена онлайн. Ако искате да я смените, свържете се с обекта."}
        </p>
      </div>

      <div className={styles.actionsSection}>
        <div className={classNames(styles.primaryActions, styles.stackedActions)}>
          {canReplace && (
            <button className={styles.primaryButton} onClick={onReplace} type="button">
              <span className={styles.buttonIcon}>🔄</span>
              <span className={styles.buttonText}>Смени резервацията</span>
            </button>
          )}

          <button
            className={canReplace ? styles.secondaryButton : styles.primaryButton}
            onClick={onKeep}
            type="button"
          >
            <span className={styles.buttonIcon}>✓</span>
            <span className={styles.buttonText}>Запази текущата</span>
          </button>
        </div>
      </div>
    </div>
  );
};

interface IWaitingListConflictKeptProps extends INavigationProps {
  result: IConfirmWaitingListSpotResultModel | null;
}

/** Shown after the client chose to keep their existing reservation. */
export const WaitingListConflictKept = ({
  result,
  onGoToMyReservations,
  onGoToCalendar,
}: IWaitingListConflictKeptProps) => (
  <div className={styles.container}>
    <div className={styles.successAnimation}>
      <div className={styles.successIcon}>
        <span className={styles.checkmark}>✓</span>
      </div>

      <h1 className={styles.successTitle}>Запазихме текущата ви резервация.</h1>
      <p className={styles.successSubtitle}>
        {result?.WaitingListEventName
          ? `Оставате в списъка на чакащите за ${result.WaitingListEventName}.`
          : "Оставате в списъка на чакащите за другия клас."}
      </p>
    </div>

    <NavigationActions
      onGoToMyReservations={onGoToMyReservations}
      onGoToCalendar={onGoToCalendar}
    />
  </div>
);

interface IWaitingListConflictLoginProps {
  onLoginSuccess: () => void;
  // Google login leaves the page, so it must come back to this link to show the choice.
  returnUrl: string;
}

/** The spot clashes with another reservation, but details and the swap need the client's own login. */
export const WaitingListConflictLogin = ({
  onLoginSuccess,
  returnUrl,
}: IWaitingListConflictLoginProps) => (
  <div className={styles.container}>
    <div className={styles.successAnimation}>
      <h1 className={styles.successTitle}>Освободи се място!</h1>
      <p className={styles.successSubtitle}>
        Имате друга резервация, която се засича с това място. Влезте в профила
        си, за да изберете коя да запазите.
      </p>
    </div>

    <div className={styles.authForm}>
      <AuthenticationForm
        onLoginSuccess={() => onLoginSuccess()}
        returnUrl={returnUrl}
        showToasts={true}
        showGoogleLogin={true}
        showPolicyDialog={true}
        formClassName={styles.authFormInner}
        fieldsClassName={styles.authFields}
        buttonClassName={classNames(styles.primaryButton, styles.authButton)}
        googleButtonClassName={classNames(styles.secondaryButton, styles.authButton)}
        compact={false}
      />
    </div>
  </div>
);

/** Logged in, but not as the client the waiting-list email was sent to. */
export const WaitingListConflictWrongAccount = ({
  onGoToMyReservations,
  onGoToCalendar,
}: INavigationProps) => (
  <div className={styles.container}>
    <div className={styles.errorAnimation}>
      <div className={styles.errorIcon}>
        <span className={styles.cross}>!</span>
      </div>

      <h1 className={styles.errorTitle}>Мястото е за друг профил</h1>
      <p className={styles.errorSubtitle}>
        Освободеното място е за профила, до който беше изпратен имейлът, и той
        има друга резервация по същото време.
      </p>
      <p className={styles.errorMessage}>
        Влезте с този профил, за да изберете коя резервация да запазите.
      </p>
    </div>

    <NavigationActions
      onGoToMyReservations={onGoToMyReservations}
      onGoToCalendar={onGoToCalendar}
    />
  </div>
);

const NavigationActions = ({
  onGoToMyReservations,
  onGoToCalendar,
}: INavigationProps) => (
  <div className={styles.actionsSection}>
    <div className={styles.primaryActions}>
      <button className={styles.primaryButton} onClick={onGoToMyReservations} type="button">
        <span className={styles.buttonIcon}>📊</span>
        <span className={styles.buttonText}>Моите резервации</span>
      </button>

      <button className={styles.secondaryButton} onClick={onGoToCalendar} type="button">
        <span className={styles.buttonIcon}>📅</span>
        <span className={styles.buttonText}>Към календара</span>
      </button>
    </div>
  </div>
);
